import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, FileWarning, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ApiClientError } from '../../services/apiClient';
import {
  listAlertasDocumentos,
  recalcularAlertasDocumentos,
} from '../../services/alertasApi';
import { AlertaDocumento, AlertaDocumentoStatus } from '../../types/alertas';

const statusLabels: Record<AlertaDocumentoStatus, string> = {
  vencido: 'Vencido',
  vencendo: 'Vencendo',
  pendente_validacao: 'Pendente de validação',
  invalido: 'Inválido',
};

const statusClasses: Record<AlertaDocumentoStatus, string> = {
  vencido: 'bg-red-50 text-red-800 border-red-200',
  vencendo: 'bg-amber-50 text-amber-800 border-amber-200',
  pendente_validacao: 'bg-blue-50 text-blue-800 border-blue-200',
  invalido: 'bg-gray-100 text-gray-800 border-gray-300',
};

function formatError(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `${error.code}: ${error.message}`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Não foi possível carregar alertas.';
}

const AlertasDocumentosPage: React.FC = () => {
  const { accessToken, hasPermission } = useAuth();
  const [items, setItems] = useState<AlertaDocumento[]>([]);
  const [status, setStatus] = useState<AlertaDocumentoStatus | ''>('');
  const [papel, setPapel] = useState('');
  const [tipoDocumento, setTipoDocumento] = useState('');
  const [diasVencimento, setDiasVencimento] = useState(30);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canRecalculate = hasPermission('documentos.validar');

  const totals = useMemo(() => {
    return items.reduce<Record<AlertaDocumentoStatus, number>>(
      (acc, item) => ({
        ...acc,
        [item.status]: acc[item.status] + 1,
      }),
      { vencido: 0, vencendo: 0, pendente_validacao: 0, invalido: 0 }
    );
  }, [items]);

  const load = useCallback(async () => {
    if (!accessToken) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await listAlertasDocumentos(accessToken, {
        status: status || undefined,
        papel: papel || undefined,
        tipoDocumento: tipoDocumento || undefined,
        diasVencimento,
      });
      setItems(response.items);
    } catch (loadError: unknown) {
      setError(formatError(loadError));
    } finally {
      setLoading(false);
    }
  }, [accessToken, diasVencimento, papel, status, tipoDocumento]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRecalcular = async () => {
    if (!accessToken) {
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const result = await recalcularAlertasDocumentos(accessToken, diasVencimento);
      setMessage(
        `Recálculo concluído: ${result.documentosMarcadosVencidos} documento(s) marcado(s) como vencido(s).`
      );
      await load();
    } catch (recalcError: unknown) {
      setError(formatError(recalcError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Alertas documentais</h2>
          <p className="mt-1 text-sm text-gray-600">
            Documentos vencidos, próximos do vencimento, pendentes ou inválidos com impacto na cadeia ativa.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            <RefreshCw size={16} />
            Atualizar
          </button>
          <button
            type="button"
            onClick={handleRecalcular}
            disabled={loading || !canRecalculate}
            className="inline-flex items-center gap-2 rounded-md bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:bg-gray-300"
          >
            <FileWarning size={16} />
            Recalcular
          </button>
        </div>
      </div>

      <section className="grid gap-3 md:grid-cols-4">
        {(Object.keys(statusLabels) as AlertaDocumentoStatus[]).map((key) => (
          <div key={key} className={`rounded-md border p-4 ${statusClasses[key]}`}>
            <div className="text-sm font-medium">{statusLabels[key]}</div>
            <div className="mt-2 text-3xl font-semibold">{totals[key]}</div>
          </div>
        ))}
      </section>

      <section className="grid gap-3 rounded-md border border-gray-200 bg-white p-4 md:grid-cols-4">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
            Status
          </label>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as AlertaDocumentoStatus | '')}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Todos</option>
            {(Object.keys(statusLabels) as AlertaDocumentoStatus[]).map((key) => (
              <option key={key} value={key}>{statusLabels[key]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
            Papel
          </label>
          <select
            value={papel}
            onChange={(event) => setPapel(event.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Todos</option>
            <option value="gerador">Gerador</option>
            <option value="transportador">Transportador</option>
            <option value="tratador">Tratador</option>
            <option value="destinador">Destinador</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
            Tipo de documento
          </label>
          <input
            value={tipoDocumento}
            onChange={(event) => setTipoDocumento(event.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            placeholder="Licença, alvará..."
          />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
            Janela de vencimento
          </label>
          <input
            type="number"
            min={1}
            max={365}
            value={diasVencimento}
            onChange={(event) => setDiasVencimento(Number(event.target.value))}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
      </section>

      {message ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          {message}
        </div>
      ) : null}

      {error ? (
        <div className="flex gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      ) : null}

      <section className="overflow-hidden rounded-md border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700">
          {loading ? 'Carregando alertas...' : `${items.length} alerta(s) encontrado(s)`}
        </div>
        <div className="divide-y divide-gray-100">
          {items.length === 0 && !loading ? (
            <div className="p-6 text-sm text-gray-500">Nenhum alerta documental encontrado.</div>
          ) : null}
          {items.map((item) => (
            <article key={item.documentoId} className="grid gap-3 p-4 lg:grid-cols-[1.5fr_1fr_1fr]">
              <div>
                <div className="font-medium text-gray-900">{item.tipoDocumento}</div>
                <div className="mt-1 text-sm text-gray-600">{item.empresaRazaoSocial}</div>
                <div className="mt-1 text-xs text-gray-500">
                  Documento {item.numeroDocumento ?? item.documentoId}
                </div>
              </div>
              <div>
                <span className={`inline-flex rounded-md border px-2 py-1 text-xs font-medium ${statusClasses[item.status]}`}>
                  {statusLabels[item.status]}
                </span>
                <div className="mt-2 text-xs text-gray-500">
                  Validade: {item.dataValidade ? new Date(item.dataValidade).toLocaleDateString('pt-BR') : 'sem data'}
                </div>
              </div>
              <div className="text-sm text-gray-600">
                {item.leitosAfetados.length === 0 ? (
                  <span>Sem leito ativo afetado.</span>
                ) : (
                  <div>
                    <div className="font-medium text-gray-800">
                      {item.leitosAfetados.length} leito(s) afetado(s)
                    </div>
                    <div className="mt-1 space-y-1">
                      {item.leitosAfetados.slice(0, 3).map((leito) => (
                        <div key={`${item.documentoId}-${leito.leitoId}-${leito.papel}`}>
                          {leito.codigoLeito} · {leito.papel}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AlertasDocumentosPage;
